import {Injectable, inject} from '@angular/core';
import {NonNullableFormBuilder, FormGroup, Validators} from '@angular/forms';
import {Store} from '@ngrx/store';
import {Observable, Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import {Project} from 'src/app/core/model/project';
import {ProjectTask} from 'src/app/core/model/task';
import {selectTeamsMap} from 'src/app/core/store/teams/teams.selector';
import {Team} from 'src/app/teams/shared/model/team';
import {TimezoneService} from 'src/app/services/timezone.service';
import {EMPTY_PROJECT} from 'src/app/projects/shared/projects-constants';

@Injectable({
  providedIn: 'root',
})
export class ProjectsService {
  private store = inject(Store);
  private formBuilder = inject(NonNullableFormBuilder);
  private timezoneService = inject(TimezoneService);
  private subject = new Subject<void>();

  getProjectPartial(project: Project): Partial<Project> {
    return {
      startDateTime: project.startDateTime,
      endDateTime: project.endDateTime,
      status: project.status,
    };
  }

  getProjectFields(project: Project): Partial<Project> {
    return {
      endDateTime: project.endDateTime,
      startDateTime: project.startDateTime,
      status: project.status,
    };
  }

  // --------------------------------------------------------------------------

  sendClickEvent() {
    this.subject.next();
  }

  getClickEvent(): Observable<unknown> {
    return this.subject.asObservable();
  }

  getAllTasksInProject(project: Project): ProjectTask[] {
    return (
      project?.steps?.reduce((tasks, step) => {
        if (step.tasks) {
          tasks.push(...step.tasks);
        }
        return tasks;
      }, []) || []
    );
  }

  getProjectUsers(project: Project): string[] {
    let teamsMap: Record<string, Team> = {};

    // Get current teams map synchronously
    this.store
      .select(selectTeamsMap)
      .pipe(take(1))
      .subscribe(map => (teamsMap = map));

    const projectUsers = project.users || [];
    const teamUsers = (project.teams || []).flatMap(teamId => {
      const team = teamsMap[teamId];
      return team?.users?.map(user => user._id).filter(Boolean) || [];
    });

    const allUsers = [...projectUsers, ...teamUsers];
    return Array.from(new Set(allUsers));
  }

  getProjectForm(project?: Project, isChallenge = false): FormGroup {
    const defaultProject = project || EMPTY_PROJECT;

    const challengeFormGroup = this.formBuilder.group({
      goals: [defaultProject.challengeBuilder?.goals || []],
      objectives: [defaultProject.challengeBuilder?.objectives || []],
      tasks: [defaultProject.challengeBuilder?.tasks || []],
    });
    return this.formBuilder.group({
      label: [defaultProject.label || 'Untitled_1', Validators.required],
      color: [defaultProject.color || '#B8B8B8'],
      startDateTime: [defaultProject.startDateTime],
      endDateTime: [defaultProject.endDateTime],
      status: [defaultProject.status || 'active'],
      program: [defaultProject.program || []],
      steps: [defaultProject.steps || []],
      ownerId: [defaultProject.ownerId || ''],
      users: [defaultProject.users || []],
      teams: [defaultProject.teams || []],
      noEndDate: [defaultProject.noEndDate || false],
      timezone: [defaultProject.timezone || this.timezoneService.getTimeZone()],
      targets: [defaultProject.targets || []],
      isChallenge: [isChallenge || defaultProject.isChallenge || false],
      challenge: challengeFormGroup,
      cycleLength: [defaultProject.challengeBuilder?.cycleLength || null],
      cyclesPerLevel: [defaultProject.winConditions?.levelProgressThreshold || null],
      cycleSuccessScore: [defaultProject.winConditions?.cycleSuccessScore || null],
    });
  }
}
